/**
 * F-17-marks AUTHORING, the parts both editing surfaces share.
 *
 * Marks are rendered by ui/MarkedText.tsx and stored by @kola/shared
 * text-marks.ts; this is the bit in between — what a bar has to show for the
 * range under the caret, and which keystroke means which mark. It lives here,
 * pure and DOM-free, because there are now TWO places a writer can format
 * text — the script block (F-28) and the grid's cell editor (F-12) — and the
 * two must agree on every answer: the same shortcuts, the same "is this
 * selection bold?" rule, the same idea of an empty range.
 */
import {
  rangeFullyMarked,
  rangeMarkColor,
  type TextMark,
  type TextMarkKind,
} from "@kola/shared";

/** What a format bar shows as active for the range under the caret. */
export interface TextFormatState {
  bold: boolean;
  strike: boolean;
  /** The one ink color over the WHOLE range, or undefined (none / mixed). */
  color: string | undefined;
  /** The one highlight color over the whole range, or undefined. */
  hl: string | undefined;
}

/** A selection's offsets in low-to-high order, or null when it is a caret. */
export function orderedRange(a: number, b: number): { start: number; end: number } | null {
  const start = Math.min(a, b);
  const end = Math.max(a, b);
  return end <= start ? null : { start, end };
}

/**
 * The two toggles everybody already has in their fingers: ⌘/Ctrl+B for bold,
 * ⌘/Ctrl+⇧X for strike-through.
 *
 * Alt-modified chords are deliberately NOT ours — on Windows and Linux
 * Ctrl+Alt is how AltGr types a character, and swallowing it would stop a
 * writer entering one. Callers gate on there being a SELECTION first: with a
 * bare caret these fall through to the browser (⌘B does nothing in a textarea
 * anyway) rather than eating a key the writer may have meant for something
 * else.
 */
export function formatShortcut(event: {
  key: string;
  shiftKey: boolean;
  altKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
}): "bold" | "strike" | null {
  if (!(event.metaKey || event.ctrlKey) || event.altKey) return null;
  const key = event.key.toLowerCase();
  if (key === "b" && !event.shiftKey) return "bold";
  if (key === "x" && event.shiftKey) return "strike";
  return null;
}

/**
 * What the bar shows for `[start, end)` of `text`.
 *
 * `read` hands back the cell's STORED marks of one kind; they are resolved
 * against `text` — which is the text the writer is looking at (an
 * uncommitted draft, mid-IME included), not necessarily what the doc holds
 * yet. That is the point of quote anchoring: a keystroke that has not been
 * committed shifts nothing here.
 */
export function readFormatState(
  text: string,
  read: (kind: TextMarkKind) => readonly TextMark[],
  start: number,
  end: number,
): TextFormatState {
  return {
    bold: rangeFullyMarked(text, read("bold"), start, end),
    strike: rangeFullyMarked(text, read("strike"), start, end),
    color: rangeMarkColor(text, read("color"), start, end),
    hl: rangeMarkColor(text, read("hl"), start, end),
  };
}
